<script>
import { convertArrayOfObjsToObj, useGetTranslation } from "./functions";
import Dialog from "./Dialog.vue";

const t = useGetTranslation();
// Can't access locally declared variables in defineProps
// we have to define the variables in a separate script then access
// them
const availableItemsMsg = t("availableItemsMsg");
const assignedItemsMsg = t("assignedItemsMsg");
const transferAllItemsMsg = t("transferAllItemsMsg");
const transferAllItemsConfirmationMsg = t("transferAllItemsConfirmationMsg");
const removeAllItemsMsg = t("removeAllItemsMsg");
const removeAllItemsConfirmationMsg = t("removeAllItemsConfirmationMsg");
</script>
<script setup>
import { computed, getCurrentInstance, reactive, ref, watch } from "vue";
import TransferListSection from "./TransferListSection.vue";

const props = defineProps({
  items: {
    type: Object,
  },
  value: {
    type: Object,
  },
  fetchingItems: {
    type: Boolean,
    default: false,
  },
  availableItemsTitle: {
    type: String,
    default: availableItemsMsg,
  },
  assignedItemsTitle: {
    type: String,
    default: assignedItemsMsg,
  },
  transferAllItemsTitle: {
    type: String,
    default: transferAllItemsMsg,
  },
  transferAllItemsConfirmation: {
    type: String,
    default: transferAllItemsConfirmationMsg,
  },
  removeAllItemsTitle: {
    type: String,
    default: removeAllItemsMsg,
  },
  removeAllItemsConfirmation: {
    type: String,
    default: removeAllItemsConfirmationMsg,
  },
  itemLabel: {
    type: String,
    default: "label",
  },
  itemValue: {
    type: String,
    default: "value",
  },
});

const emit = defineEmits(["input"]);

const innerValue = computed({
  get: () => {
    return props.value;
  },
  set: (val) => {
    emit("input", val);
  },
});

const transferOrRemoveDialogData = reactive({
  dialogOpen: false,
  title: "",
  text: "",
  action: () => {},
});

const transferrableItems = computed(() => {
  const itemsCopy = { ...props.items };
  Object.keys(props.value).forEach((key) => {
    if (!!itemsCopy[key]) {
      delete itemsCopy[key];
    }
  });
  return itemsCopy;

  // Object.entries({ ...props.items })
  //   .filter((key, _) => props.value[key] === undefined)
  //   .map();
});

const selectedItemsToTransfer = ref([]);
const selectedItemsToRemove = ref([]);

function getSelectedItems() {
  return convertArrayOfObjsToObj(
    selectedItemsToTransfer.value.map((key) => props.items[key]),
    props.itemValue
  );
}

function transferSelectedItems() {
  const itemsToTransfer = getSelectedItems();
  innerValue.value = { ...itemsToTransfer, ...innerValue.value };
  selectedItemsToTransfer.value = [];
  selectedItemsToRemove.value = [];
}

function removeSelectedItems() {
  const valueCopy = { ...innerValue.value };
  selectedItemsToRemove.value
    .map((key) => props.items[key][props.itemValue])
    .forEach((key) => {
      delete valueCopy[key];
    });
  innerValue.value = valueCopy;
  selectedItemsToRemove.value = [];
  selectedItemsToTransfer.value = [];
}

function onTransferAllItemsClicked() {
  transferOrRemoveDialogData.title = props.transferAllItemsTitle;
  transferOrRemoveDialogData.text = props.transferAllItemsConfirmation;
  transferOrRemoveDialogData.action = () => {
    innerValue.value = { ...transferrableItems.value, ...innerValue.value };
    transferOrRemoveDialogData.dialogOpen = false;
    selectedItemsToRemove.value = [];
    selectedItemsToTransfer.value = [];
  };
  transferOrRemoveDialogData.dialogOpen = true;
}

function onRemoveAllItemsClicked() {
  transferOrRemoveDialogData.title = props.removeAllItemsTitle;
  transferOrRemoveDialogData.text = props.removeAllItemsConfirmation;
  transferOrRemoveDialogData.action = () => {
    innerValue.value = {};
    transferOrRemoveDialogData.dialogOpen = false;
    selectedItemsToRemove.value = [];
    selectedItemsToTransfer.value = [];
  };
  transferOrRemoveDialogData.dialogOpen = true;
}
</script>

<template>
  <v-container>
    <v-row class="">
      <v-col cols="12" md="5">
        <v-skeleton-loader
          v-if="fetchingItems"
          type="image"
          height="30vh"
          class="skeleton-fill-width skeleton-fill-height"
        />
        <TransferListSection
          v-else
          :items="transferrableItems"
          :selectedItems.sync="selectedItemsToTransfer"
          :title="availableItemsTitle"
          :itemLabel="itemLabel"
          :itemValue="itemValue"
        />
      </v-col>
      <v-col
        cols="12"
        md="2"
        class="d-flex flex-column align-center justify-center"
        :style="{ gap: '16px' }"
      >
        <v-skeleton-loader
          v-if="fetchingItems"
          type="image"
          height="20vh"
          width="88px"
          class="skeleton-fill-width skeleton-fill-height"
        />
        <template v-else>
          <v-btn icon class="primary white--text" @click="transferSelectedItems"
            ><v-icon>mdi-chevron-right</v-icon></v-btn
          >
          <v-btn
            icon
            class="primary white--text"
            @click="onTransferAllItemsClicked"
            ><v-icon>mdi-chevron-double-right</v-icon></v-btn
          >
          <v-btn icon class="primary white--text" @click="removeSelectedItems"
            ><v-icon>mdi-chevron-left</v-icon></v-btn
          >
          <v-btn
            icon
            class="primary white--text"
            @click="onRemoveAllItemsClicked"
            ><v-icon>mdi-chevron-double-left</v-icon></v-btn
          >
        </template>
      </v-col>
      <v-col cols="12" md="5">
        <v-skeleton-loader
          v-if="fetchingItems"
          type="image"
          height="30vh"
          class="skeleton-fill-width skeleton-fill-height"
        />
        <TransferListSection
          v-else
          :items="value"
          :selectedItems.sync="selectedItemsToRemove"
          :title="assignedItemsTitle"
          :itemLabel="itemLabel"
          :itemValue="itemValue"
        />
      </v-col>
    </v-row>

    <Dialog
      :dialogOpen.sync="transferOrRemoveDialogData.dialogOpen"
      max-width="800px"
    >
      <template #title>{{ transferOrRemoveDialogData.title }}</template>
      <template #text>
        <div class="d-flex flex-column">
          {{ transferOrRemoveDialogData.text }}
          <v-btn
            color="success"
            class="text-none align-self-end"
            depressed
            @click="transferOrRemoveDialogData.action()"
            >{{ $t("yesMsg") }}</v-btn
          >
        </div>
      </template>
    </Dialog>
  </v-container>
</template>
